import { action, atom, noop } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { User } from '@root/entities/auth'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { AuthErrorCode } from '@root/shared/api/api-error-code'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'

import { WrongOtpError } from '../auth-errors'

import type { Ctx } from '@reatom/framework'
import type { Error } from '@root/shared'

export const verifyOtpRequestAction = action(
  async (ctx, { code }: { code: string }) => {
    const {
      tradeApiClient,
      environment: { assets },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.otp.userOtpVerifyCreate, [
        { token: code },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => User.fromDto(it, assets.avatarsHost)),
    )
  },
  'verifyOtpRequestAction',
)

export type OpenOtpModalArgs = {
  onOtp: (ctx: Ctx, args: { code: string }) => void
}

export const openOtpModalEvent = action((ctx, args: OpenOtpModalArgs) => {
  const stateAtom = atom<
    AsyncStateN.AsyncStateN<WrongOtpError | Error.UnknownError, unknown>
  >(AsyncStateN.notInitiated(), 'stateAtom')
  const setStateAction = action(
    (
      ctx,
      state: AsyncStateN.AsyncStateN<
        WrongOtpError | Error.UnknownError,
        unknown
      >,
    ) => {
      stateAtom(ctx, state)
    },
    'setStateAction',
  )
  const resetStateAction = action((ctx) => {
    stateAtom(ctx, AsyncStateN.notInitiated())
  }, 'resetStateAction')

  const otpEnteredEvent = action((ctx, { code }: { code: string }) => {
    args.onOtp(ctx, { code })
  }, 'otpEnteredEvent')

  const closeAction = action(noop, 'closeAction')
  const resetAction = action(noop, 'resetAction')

  return {
    otpEnteredEvent,
    closeAction,
    resetAction,
    stateAtom,
    setState: setStateAction,
    resetState: resetStateAction,
  }
}, 'openOtpModalEvent')

export type OpenOtpModalController = ReturnType<typeof openOtpModalEvent>

export const requestOtpVerificationAction = action((ctx) => {
  const controller = openOtpModalEvent(ctx, {
    onOtp: async (ctx, { code }) => {
      controller.setState(ctx, AsyncStateN.loading())
      const result = await verifyOtpRequestAction(ctx, { code })

      if (Result.isSuccess(result)) {
        controller.closeAction(ctx)
        controller.setState(ctx, AsyncStateN.success(undefined))
        return
      }

      if (ApiError.isOfCode(AuthErrorCode.InvalidOTP)(result.failure)) {
        controller.setState(ctx, AsyncStateN.error(new WrongOtpError()))
        return
      }

      showErrorSnackbarAction(ctx, result.failure)
      controller.resetAction(ctx)
    },
  })
}, 'requestOtpVerificationAction')
